import { useEffect, useState } from "react";
import { Col, Row, Tab, Tabs } from "react-bootstrap";
import DonutChartCarousel from "../../../../../components/donutChartCarousel/DonutChartCarousel";
import { IDonutChartWithLabelProps } from "../../../../../components/donutChartWithLabel/DonutChartWithLabel";
import EmptyMessage from "../../../../../components/emptyMessage/EmptyMessage";
import EmptyPlaceholder from "../../../../../components/emptyPlaceholder/EmptyPlaceholder";
import StatusAlertModel from "../../../../../components/statusAlertModel/StatusAlertModel";
import {
  IBroadbandUsagePackageInfo,
  IDashboardSummary,
  IDashboardSummaryUsageDetailsItem,
} from "../../../../../interfaces/dashboardSummary";
import { PROFILE_ACTIONS } from "../../../../../interfaces/profileData";
import { ISuccessState } from "../../../../../interfaces/successState";
import {
  getBroadbandMonthlyOtherDataUsage,
  getDashboardSummary,
} from "../../../../../services/dashboard";
import { gbToBytes } from "../../../../../util/gbToBytes";
import { getDataWithCheckingActionPrivilege } from "../../../../../util/hasActionPrivileges";
import styles from "./BroadbandUsage.module.scss";

interface IProps {
  selectedCircuitId: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const BroadbandUsage = (props: IProps) => {
  const { selectedCircuitId, setLoading } = props;
  const [activeTabKey, setActiveTabKey] = useState<string>("myPackage");
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();
  const [donutChartData, setDonutChartData] =
    useState<IDonutChartWithLabelProps[]>();

  useEffect(() => {
    if (selectedCircuitId) {
      setDonutChartData([]);
      if (activeTabKey === "myPackage") {
        setLoading(true);
        getDataWithCheckingActionPrivilege(
          true,
          selectedCircuitId,
          PROFILE_ACTIONS.USAGE,
          () => getDashboardSummary(selectedCircuitId)
        )
          .then((data: IDashboardSummary) => {
            setDonutChartData(
              getDonutChartData(data?.my_Package_Info, data?.status)
            );
            setLoading(false);
          })
          .catch((err) => {
            setIsSuccess({ isSuccess: false, message: err.message });
            setLoading(false);
          });
      } else if (activeTabKey) {
        setLoading(true);
        getDataWithCheckingActionPrivilege(
          true,
          selectedCircuitId,
          PROFILE_ACTIONS.USAGE,
          () =>
            getBroadbandMonthlyOtherDataUsage(
              selectedCircuitId,
              activeTabKey as "bonus" | "extragb" | "free"
            )
        )
          .then((data: IBroadbandUsagePackageInfo) => {
            setDonutChartData(getDonutChartData(data));
            setLoading(false);
          })
          .catch((err) => {
            setIsSuccess({ isSuccess: false, message: err.message });
            setLoading(false);
          });
      }
    }
  }, [selectedCircuitId, activeTabKey, setLoading]);

  const getDonutChartData = (
    dataB: IBroadbandUsagePackageInfo,
    status?: string
  ): IDonutChartWithLabelProps[] => {
    return dataB?.usageDetails?.map(
      (data: IDashboardSummaryUsageDetailsItem) => {
       
// Handle expiry_Date - safely parse any format, return undefined if invalid

// let expiryDate: string | undefined = undefined;
// try {
//   if (data?.expiry_Date && data.expiry_Date.trim() !== '') {
//     const date = new Date(data.expiry_Date);
//     if (!isNaN(date.getTime())) {
//       expiryDate = date.toISOString();
//     }
//   }
// } catch (error) {
//   // Silently handle any parsing errors, expiryDate remains undefined
//   expiryDate = undefined;
// }

// return {
//   totalQuotaBytes: parseFloat(data?.limit?.replace(/,/g, "")),
//   remainQuotaBytes: parseFloat(data?.remaining?.replace(/,/g, "")),
//   expiryDate: expiryDate, // Will be undefined if null/empty/invalid format
//   title: (

        return {
          totalQuotaBytes: parseFloat(data?.limit?.replace(/,/g, "")),
          remainQuotaBytes: parseFloat(data?.remaining?.replace(/,/g, "")),
          expiryDate: new Date(data?.expiry_Date).toISOString(),
          
          title: (
            <>
              {status && (
                <>
                  <Row>
                    <Col className={[styles.status, "mb-2"].join(" ")}>
                      <span className="fw-bold">{dataB.package_Name}</span>
                    </Col>
                  </Row>
                  <Row>
                    <Col className={[styles.status, "mb-2"].join(" ")}>
                      Your speed is <span className="fw-bold">{status}</span>{" "}
                      right now
                    </Col>
                  </Row>
                </>
              )}
              <Row>
                <Col className={styles.status}>{data.name}</Col>
              </Row>
            </>
          ),
          data: [
            {
              label: `REMAINING`,
              value: +data.percentage,
            },
            {
              label: `USED`,
              value: 100 - +data.percentage,
            },
          ],
        };
      }
    );
  };

  const getDonutChart = (
    chartData: IDonutChartWithLabelProps[] | undefined,
    dataType: string
  ): JSX.Element => {
    return chartData && chartData?.length <= 0 ? (
      <EmptyMessage show message={`You don't have ${dataType}.`} />
    ) : (
      <DonutChartCarousel donutChartData={donutChartData} />
    );
  };

  const dataTypeTabs: {
    eventKey: string;
    title: string;
    child: (
      chartData: IDonutChartWithLabelProps[] | undefined,
      dataType: string
    ) => JSX.Element;
  }[] = [
    {
      eventKey: "myPackage",
      title: "My Package",
      child: getDonutChart,
    },
    {
      eventKey: "extragb",
      title: "Extra GB",
      child: getDonutChart,
    },
    {
      eventKey: "bonus",
      title: "Bonus Data",
      child: getDonutChart,
    },
    {
      eventKey: "free",
      title: "Free Data",
      child: getDonutChart,
    },
  ];

  return (
    <>
      {selectedCircuitId ? (
        <Tabs
          defaultActiveKey="myPackage"
          activeKey={activeTabKey as any}
          onSelect={(k) => k && setActiveTabKey(k)}
          className="my-3"
          justify
        >
          {dataTypeTabs.map((tab, index) => (
            <Tab key={index} eventKey={tab.eventKey} title={tab.title}>
              {tab.child(donutChartData, tab.title)}
            </Tab>
          ))}
        </Tabs>
      ) : (
        <EmptyPlaceholder className="mt-5" />
      )}
      <StatusAlertModel isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
    </>
  );
};

export default BroadbandUsage;
